import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import type { Rarity, CollectionResponse } from "../types";
import { CharacterCard } from "./CharacterCard";

interface CollectionProps {
  data: CollectionResponse | null;
  loading: boolean;
  onGoToShop: () => void;
}

type Filter = Rarity | "all" | "active" | "idle";

export function Collection({ data, loading, onGoToShop }: CollectionProps) {
  const [filter, setFilter] = useState<Filter>("all");

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <p className="text-muted-foreground animate-pulse">
          Loading collection...
        </p>
      </div>
    );
  }

  if (!data || data.cards.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-4 px-4 text-center">
        <h3 className="text-xl font-semibold text-muted-foreground">
          No cards yet
        </h3>
        <p className="text-sm text-muted-foreground">
          Open some packs to start your collection.
        </p>
        <Button type="button" onClick={onGoToShop}>
          Open Your First Pack
        </Button>
      </div>
    );
  }

  const cards = data.cards;
  const filtered =
    filter === "all"
      ? cards
      : filter === "active"
        ? cards.filter((c) => c.is_active === 1)
        : filter === "idle"
          ? cards.filter((c) => c.is_active === 0)
          : cards.filter((c) => c.rarity === filter);

  const filters: { id: Filter; label: string; class?: string }[] = [
    { id: "all", label: "All" },
    { id: "active", label: "Active" },
    { id: "idle", label: "Idle" },
    { id: "legendary", label: "Legendary", class: "bg-gradient-to-r from-yellow-500 to-amber-500 text-black" },
    { id: "epic", label: "Epic", class: "bg-purple-600 text-white hover:bg-purple-600" },
    { id: "rare", label: "Rare", class: "bg-blue-600 text-white hover:bg-blue-600" },
    { id: "uncommon", label: "Uncommon", class: "bg-emerald-600 text-white hover:bg-emerald-600" },
    { id: "common", label: "Common" },
  ];

  const progressPct = (data.stats.uniqueCards / data.stats.totalCharacters) * 100;

  return (
    <div className="flex flex-col gap-6 py-8 px-4 max-w-5xl mx-auto">
      <div className="text-center space-y-1">
        <h2 className="text-2xl font-bold tracking-tight">Collection</h2>
        <p className="text-sm text-muted-foreground">
          {data.stats.uniqueCards} / {data.stats.totalCharacters} unique
          &middot; {data.stats.totalCards} total cards
        </p>
      </div>

      <div className="w-full max-w-md mx-auto space-y-1">
        <Progress value={progressPct} className="h-2" />
        <p className="text-xs text-muted-foreground text-center">
          {Math.round(progressPct)}% complete
        </p>
      </div>

      <div className="flex flex-wrap justify-center gap-1.5">
        {filters.map((f) => (
          <Badge
            key={f.id}
            asChild
            variant={filter === f.id ? "default" : "outline"}
            className={`cursor-pointer text-xs ${filter === f.id && f.class ? f.class : ""}`}
          >
            <button
              type="button"
              onClick={() => setFilter(f.id)}
              aria-pressed={filter === f.id}
            >
              {f.label}
            </button>
          </Badge>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-muted-foreground text-sm">
            No {filter} cards yet.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 justify-items-center">
          {filtered.map((card) => (
            <div key={card.id} className="relative">
              <CharacterCard
                character={{
                  id: card.character_id,
                  name: card.name,
                  nickname: card.nickname,
                  rarity: card.rarity,
                  description: card.description,
                  image_url: card.image_url,
                }}
                variant={card.variant}
                compact
              />
              {card.is_active === 1 && (
                <Badge
                  className="absolute -top-1 -right-1 text-[10px] h-5 px-1.5 flex items-center justify-center rounded-full z-10 bg-green-500 text-white"
                >
                  ACTIVE
                </Badge>
              )}
              {card.variant !== "normal" && (
                <Badge
                  className={`absolute -bottom-1 left-1/2 -translate-x-1/2 text-[9px] h-4 px-1.5 z-10 ${
                    card.variant === "shiny"
                      ? "bg-slate-300 text-slate-900"
                      : card.variant === "rainbow"
                        ? "bg-gradient-to-r from-red-400 via-green-400 to-blue-400 text-white"
                        : "bg-green-500 text-black"
                  }`}
                >
                  {card.variant}
                </Badge>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
